/**
 * Error codes — ใช้ร่วมกันระหว่าง Worker และ frontend
 * frontend แปลเป็นข้อความไทยจาก ERROR_MESSAGE_TH ไม่ใช้ message ที่ server ส่งมาตรง ๆ
 * (server message อาจมีรายละเอียดทางเทคนิคที่ไม่ควรให้ผู้ใช้เห็น)
 */

export const ERROR_CODES = [
  // auth
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'FORBIDDEN',
  'STAFF_NOT_LINKED',
  'STAFF_INACTIVE',
  'INVALID_CREDENTIALS',
  'SSO_ACCOUNT_UNKNOWN',
  'LINE_VERIFY_FAILED',

  // linking
  'INVITE_INVALID',
  'INVITE_EXPIRED',
  'INVITE_USED',
  'LINE_ALREADY_LINKED',
  'STAFF_ALREADY_LINKED',

  // generic
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'CONFLICT',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'IDEMPOTENCY_IN_PROGRESS',

  // beneficiary
  'BENEFICIARY_DUPLICATE',
  'BENEFICIARY_CONSENT_REQUIRED',

  // staff
  'STAFF_CODE_TAKEN',
  'STAFF_EMAIL_TAKEN',
  'CANNOT_MODIFY_SELF',
  'LAST_SUPER_ADMIN',

  // เคสความช่วยเหลือ
  'CASE_INVALID_TRANSITION',
  'CASE_APPROVAL_LEVEL_TOO_LOW',
  'CASE_SELF_APPROVAL',
  'CASE_AMOUNT_EXCEEDS_LIMIT',
  'CASE_DISBURSE_EXCEEDS_APPROVED',
  'ALLOCATION_EXCEEDS_DONATION',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ERROR_MESSAGE_TH: Record<ErrorCode, string> = {
  UNAUTHENTICATED: 'กรุณาเข้าสู่ระบบ',
  TOKEN_EXPIRED: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่',
  FORBIDDEN: 'คุณไม่มีสิทธิ์ใช้งานส่วนนี้',
  STAFF_NOT_LINKED: 'บัญชี LINE นี้ยังไม่ได้ผูกกับเจ้าหน้าที่ กรุณาติดต่อผู้ดูแลระบบ',
  STAFF_INACTIVE: 'บัญชีเจ้าหน้าที่ถูกระงับหรือสิ้นสุดการทำงานแล้ว',
  INVALID_CREDENTIALS: 'เข้าสู่ระบบไม่สำเร็จ กรุณาลองใหม่',
  SSO_ACCOUNT_UNKNOWN: 'บัญชีนี้ยังไม่ได้ผูกกับเจ้าหน้าที่ กรุณาติดต่อผู้ดูแลระบบ',
  LINE_VERIFY_FAILED: 'ตรวจสอบบัญชี LINE ไม่สำเร็จ กรุณาลองใหม่',

  INVITE_INVALID: 'ลิงก์ผูกบัญชีไม่ถูกต้อง',
  INVITE_EXPIRED: 'ลิงก์ผูกบัญชีหมดอายุแล้ว กรุณาขอลิงก์ใหม่',
  INVITE_USED: 'ลิงก์นี้ถูกใช้ไปแล้ว',
  LINE_ALREADY_LINKED: 'บัญชี LINE นี้ผูกกับเจ้าหน้าที่คนอื่นอยู่แล้ว',
  STAFF_ALREADY_LINKED: 'เจ้าหน้าที่คนนี้ผูกบัญชี LINE ไว้แล้ว',

  NOT_FOUND: 'ไม่พบข้อมูลที่ต้องการ',
  VALIDATION_ERROR: 'ข้อมูลที่กรอกไม่ถูกต้อง',
  CONFLICT: 'ข้อมูลขัดแย้งกับที่มีอยู่',
  RATE_LIMITED: 'ใช้งานถี่เกินไป กรุณารอสักครู่',
  INTERNAL_ERROR: 'เกิดข้อผิดพลาดในระบบ',
  IDEMPOTENCY_IN_PROGRESS: 'คำขอก่อนหน้ากำลังประมวลผล กรุณารอสักครู่',

  BENEFICIARY_DUPLICATE: 'มีผู้รับความช่วยเหลือรายนี้ในระบบแล้ว',
  BENEFICIARY_CONSENT_REQUIRED: 'ต้องได้รับความยินยอมการเก็บข้อมูลก่อนบันทึก',

  STAFF_CODE_TAKEN: 'รหัสเจ้าหน้าที่นี้ถูกใช้แล้ว',
  STAFF_EMAIL_TAKEN: 'อีเมลนี้ถูกใช้แล้ว',
  CANNOT_MODIFY_SELF: 'ไม่สามารถแก้ไขสิทธิ์หรือสถานะของตัวเองได้',
  LAST_SUPER_ADMIN: 'ต้องมีผู้ดูแลระบบสูงสุดอย่างน้อย 1 คน',

  CASE_INVALID_TRANSITION: 'เปลี่ยนสถานะเคสแบบนี้ไม่ได้',
  CASE_APPROVAL_LEVEL_TOO_LOW: 'วงเงินนี้ต้องให้ผู้มีอำนาจระดับสูงกว่าเป็นผู้อนุมัติ',
  CASE_SELF_APPROVAL: 'อนุมัติเคสที่ตัวเองเป็นผู้ยื่นหรือผู้รับผิดชอบไม่ได้',
  CASE_AMOUNT_EXCEEDS_LIMIT: 'จำนวนเงินเกินวงเงินสูงสุดของประเภทความช่วยเหลือนี้',
  CASE_DISBURSE_EXCEEDS_APPROVED: 'ยอดจ่ายรวมเกินวงเงินที่อนุมัติไว้',
  ALLOCATION_EXCEEDS_DONATION: 'จัดสรรเกินยอดเงินบริจาคที่เหลืออยู่',
};

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: unknown;
  };
}

export function messageFor(code: string): string {
  return ERROR_MESSAGE_TH[code as ErrorCode] ?? ERROR_MESSAGE_TH.INTERNAL_ERROR;
}
